import {
  AdminApplicationStatus,
  ApplicationWithAdminData,
} from '../types/admin';
import { User } from '../types/auth';

// Status reversion mapping based on the endpoint documentation
const STATUS_REVERSION_MAP: Record<
  AdminApplicationStatus,
  AdminApplicationStatus | null
> = {
  draft: null, // Cannot be reverted
  submitted: null, // Cannot be reverted (only moves forward)
  under_admissibility_review: 'submitted',
  admissible: 'under_admissibility_review',
  not_admissible: 'under_admissibility_review',
  under_evaluation: 'admissible',
  evaluated: 'under_evaluation',
  selected: 'evaluated', // Can be reverted to evaluated
  not_selected: 'evaluated', // Can be reverted to evaluated
};

/**
 * Get the target status when reverting from current status
 */
export const getRevertTargetStatus = (
  currentStatus: AdminApplicationStatus
): AdminApplicationStatus | null => {
  return STATUS_REVERSION_MAP[currentStatus] || null;
};

/**
 * Check if a status can be reverted
 */
export const isStatusRevertible = (status: AdminApplicationStatus): boolean => {
  return STATUS_REVERSION_MAP[status] !== null;
};

/**
 * Check if user is a superadmin
 */
export const isSuperAdmin = (user: User): boolean => {
  return user.role === 'admin' && user.userProfile?.affiliation === 'TODAS';
};

/**
 * Check if user can revert a specific application status
 */
export const canUserRevertStatus = (
  application: ApplicationWithAdminData,
  user: User
): boolean => {
  const { status } = application;

  // Check if status is revertible at all
  if (!isStatusRevertible(status)) {
    return false;
  }

  // Superadmin can revert anything
  if (!isSuperAdmin(user)) {
    return false;
  }

  // Check if user is assigned reviewer for reviewer-specific statuses
  const isAssignedReviewer =
    application.reviewerAssignment?.adminId === user.id;

  // Reviewer-specific restrictions for admissible/not_admissible
  if (
    (status === 'admissible' || status === 'not_admissible') &&
    !isAssignedReviewer
  ) {
    return false;
  }

  // For evaluation statuses, only superadmins can revert
  if (
    (status === 'under_evaluation' || status === 'evaluated') &&
    !isSuperAdmin(user)
  ) {
    return false;
  }

  // For other statuses, any admin with appropriate affiliation can revert
  // Note: Affiliation-based access control is handled by the backend
  return user.role === 'admin' || user.role === 'evaluator';
};

/**
 * Get user-friendly status labels for display
 */
export const getStatusLabel = (status: AdminApplicationStatus): string => {
  const statusLabels: Record<AdminApplicationStatus, string> = {
    draft: 'Borrador',
    submitted: 'Recibida',
    under_admissibility_review: 'En revisión de admisibilidad',
    admissible: 'Admisible',
    under_evaluation: 'En evaluación',
    evaluated: 'Evaluada',
    selected: 'Adjudicada',
    not_selected: 'No adjudicada',
    not_admissible: 'No admisible',
  };

  return statusLabels[status] || status;
};

/**
 * Get confirmation message for status reversion
 */
export const getRevertConfirmationMessage = (
  currentStatus: AdminApplicationStatus,
  targetStatus: AdminApplicationStatus
): string => {
  const currentLabel = getStatusLabel(currentStatus);
  const targetLabel = getStatusLabel(targetStatus);

  return `¿Está seguro de que desea revertir esta postulación de "${currentLabel}" a "${targetLabel}"?`;
};

/**
 * Get error message based on API error status
 */
export const getRevertErrorMessage = (errorStatus?: number): string => {
  switch (errorStatus) {
    case 400:
      return 'Este estado no puede ser revertido. No se encontró un estado previo válido.';
    case 403:
      return 'No tiene permisos para revertir este estado.';
    case 404:
      return 'Aplicación no encontrada.';
    case 409:
      return 'No se puede revertir: acciones posteriores impiden esta reversión.';
    default:
      return 'Ocurrió un error inesperado. Por favor, inténtelo nuevamente.';
  }
};
